const DEEPSEEK_API_KEY = 'sk-04a8bc1279a0475eaab02f745561042b';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/chat/completions';

class DeepSeekChatAPI {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
    this.chatHistory = [];
  }

  async sendMessage(message, context = {}, retryCount = 0) {
    const { type = 'direct', textId = null, category = null, currentContent = null } = context;
    
    const systemPrompt = this.getSystemPrompt(type, context);
    const userMessage = this.formatUserMessage(message, type, context);

    const requestBody = {
      model: 'deepseek-chat',
      messages: [
        { role: 'system', content: systemPrompt },
        ...this.chatHistory,
        { role: 'user', content: userMessage }
      ],
      temperature: 0.7,
      max_tokens: 2000,
      stream: false
    };

    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('DeepSeek API Error Response:', errorData);
        
        // Retry on server errors (5xx) or rate limiting (429)
        if ((response.status >= 500 || response.status === 429) && retryCount < 3) {
          console.log(`Retrying API call... (attempt ${retryCount + 1}/3)`);
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          return this.sendMessage(message, context, retryCount + 1);
        }
        
        throw new Error(`API request failed: ${response.status} - ${errorData}`);
      }

      const data = await response.json();
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const aiResponse = data.choices[0].message.content;

      // Add to chat history
      this.chatHistory.push(
        { role: 'user', content: userMessage },
        { role: 'assistant', content: aiResponse }
      );

      // Keep only last 10 messages to manage context length
      if (this.chatHistory.length > 20) {
        this.chatHistory = this.chatHistory.slice(-20);
      }

      return {
        success: true,
        response: aiResponse,
        type: type,
        context: context,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('DeepSeek API Error:', error);
      return {
        success: false,
        error: error.message,
        type: type,
        context: context,
        timestamp: new Date().toISOString()
      };
    }
  }

  getSystemPrompt(type, context) {
    const basePrompt = `你是一位專業的中文教師，專門協助學生學習香港中學文憑考試（HKDSE）的十二篇範文。你的回答應該準確、有教育意義，並且符合HKDSE的考試要求。`;

    switch (type) {
      case 'quiz':
        return `${basePrompt}

當前任務：為學生生成關於「${context.textTitle || '指定文本'}」的測驗題目。

請生成5-10道多選題，每題包含：
1. 清晰的問題陳述
2. 四個選項（A、B、C、D）
3. 正確答案
4. 簡要解釋

題目應涵蓋：
- 文本內容理解
- 修辭手法分析
- 主題思想
- 文學技巧
- 考試重點

請以JSON格式回應，方便系統處理。`;

      case 'drill':
        return `${basePrompt}

當前任務：為學生提供「${context.textTitle || '指定文本'}」的練習題目。

請提供多種類型的練習：
1. 填空題（重要詞語、句子）
2. 問答題（理解分析）
3. 寫作練習（仿寫、續寫）
4. 修辭手法識別
5. 主題探討

每個練習都應包含：
- 題目說明
- 參考答案
- 評分標準
- 學習重點

請以結構化格式回應，便於學生練習。`;

      case 'direct':
      default:
        return `${basePrompt}

當前學習內容：${context.textTitle || '十二篇範文'}
學習階段：${context.currentView || '一般學習'}

請根據學生的問題提供詳細、準確的回答。如果問題涉及特定文本，請結合原文內容進行分析。回答應該：
1. 準確回答問題
2. 提供相關背景知識
3. 給出學習建議
4. 符合HKDSE考試要求

保持友善、耐心的教學態度。`;
    }
  }

  formatUserMessage(message, type, context) {
    let formattedMessage = message;

    if (context.textTitle) {
      formattedMessage = `[學習文本：${context.textTitle}] ${message}`;
    }

    if (context.currentContent) {
      formattedMessage += `\n\n當前學習內容：${context.currentContent}`;
    }

    return formattedMessage;
  }

  // Generate quiz questions from AI response
  parseQuizResponse(response) {
    try {
      // Try to parse JSON first
      const jsonMatch = response.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[1]);
      }

      // Fallback: parse structured text
      const questions = [];
      const lines = response.split('\n');
      let currentQuestion = null;

      for (let line of lines) {
        line = line.trim();
        if (line.match(/^\d+\./)) {
          if (currentQuestion) {
            questions.push(currentQuestion);
          }
          currentQuestion = {
            id: questions.length + 1,
            question: line.replace(/^\d+\.\s*/, ''),
            options: [],
            correct: '',
            explanation: ''
          };
        } else if (line.match(/^[ABCD]\./)) {
          if (currentQuestion) {
            currentQuestion.options.push(line.replace(/^[ABCD]\.\s*/, ''));
          }
        } else if (line.includes('答案：') || line.includes('正確答案：')) {
          if (currentQuestion) {
            currentQuestion.correct = line.split('：')[1].trim();
          }
        } else if (line.includes('解釋：') || line.includes('說明：')) {
          if (currentQuestion) {
            currentQuestion.explanation = line.split('：')[1].trim();
          }
        }
      }

      if (currentQuestion) {
        questions.push(currentQuestion);
      }

      return questions;
    } catch (error) {
      console.error('Error parsing quiz response:', error);
      return [];
    }
  }

  // Generate drill exercises from AI response
  parseDrillResponse(response) {
    const exercises = [];
    const sections = response.split(/\n\s*\n/);

    for (let section of sections) {
      if (section.trim()) {
        const lines = section.split('\n');
        const title = lines[0].replace(/^\d+\.\s*/, '').trim();
        const content = lines.slice(1).join('\n').trim();

        exercises.push({
          id: exercises.length + 1,
          title: title,
          content: content,
          type: this.identifyExerciseType(title),
          difficulty: 'intermediate'
        });
      }
    }

    return exercises;
  }

  identifyExerciseType(title) {
    if (title.includes('填空') || title.includes('填寫')) return 'fill-blank';
    if (title.includes('問答') || title.includes('回答')) return 'question-answer';
    if (title.includes('寫作') || title.includes('作文')) return 'writing';
    if (title.includes('修辭') || title.includes('手法')) return 'rhetoric';
    if (title.includes('分析') || title.includes('探討')) return 'analysis';
    return 'general';
  }

  // Clear chat history
  clearHistory() {
    this.chatHistory = [];
  }

  // Get chat history
  getHistory() {
    return this.chatHistory;
  }

  // Test API connection
  async testConnection() {
    try {
      const response = await this.sendMessage('Hello', { type: 'direct' });
      return response.success;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }

  // Export chat as different formats
  exportChat(format = 'text') {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `deepseek_chat_${timestamp}`;

    switch (format) {
      case 'json':
        return {
          filename: `${filename}.json`,
          content: JSON.stringify({
            history: this.chatHistory,
            timestamp: new Date().toISOString(),
            version: '1.0'
          }, null, 2),
          mimeType: 'application/json'
        };

      case 'markdown':
        const mdContent = this.chatHistory.map(msg => {
          const role = msg.role === 'user' ? '**學生**' : '**AI老師**';
          return `${role}：\n${msg.content}\n`;
        }).join('\n---\n\n');

        return {
          filename: `${filename}.md`,
          content: `# DeepSeek 學習對話記錄\n\n生成時間：${new Date().toLocaleString('zh-TW')}\n\n---\n\n${mdContent}`,
          mimeType: 'text/markdown'
        };

      case 'pdf':
        // Return structured data for PDF generation
        return {
          filename: `${filename}.pdf`,
          content: this.chatHistory,
          mimeType: 'application/pdf',
          metadata: {
            timestamp: new Date().toISOString(),
            totalMessages: this.chatHistory.length,
            version: '1.0'
          }
        };

      case 'text':
      default:
        const textContent = this.chatHistory.map(msg => {
          const role = msg.role === 'user' ? '學生' : 'AI老師';
          return `${role}：${msg.content}`;
        }).join('\n\n');

        return {
          filename: `${filename}.txt`,
          content: `DeepSeek 學習對話記錄\n生成時間：${new Date().toLocaleString('zh-TW')}\n\n${textContent}`,
          mimeType: 'text/plain'
        };
    }
  }
}

// Create singleton instance
const deepseekChatAPI = new DeepSeekChatAPI();

export default deepseekChatAPI; 